// components/SimulationBDC2List.tsx
import React, { useEffect, useState } from "react";
import { Button, Form, Space, Spin, Table, Tabs, Tag } from "antd";
import type { TabsProps } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { UserOutlined } from '@ant-design/icons';
import {
  Typography, Box,
} from "@mui/material";
import { SwipeUp } from '@mui/icons-material';
import axios from "axios";
import { IParametreBDC, ISimulationBDC2 } from "../../../dataTypes/GlobalDataType";
import { Crypter } from "../../../Functions/Security/Crypter";
import Data from '../../../Data/Data.json';
import { useAuthHeader, useAuthUser } from '../../../Services/Auth/sessionAuth';
import Utilisateur from "../../../Models/Utilisateur";
import { fetchDataTable2, GlobalExportToCSV, GlobalExportToExcel, GlobalExportToJSON, GlobalExportToPDF, GlobalExportToWord } from "../../../utilitaires/globalFunction";
import formatNumber from "../../../Functions/Traitement/FormatNumber";
import SweetAlert from "sweetalert2";
import ParamsPicklist from "../../../Types/ParamsPicklist";
import InputForm from "../../InputT";
import SearchInput from "../../SearchInput";
import SearchModal from "../../SearchModal";
import ExportMenu from "../../ExportMenu";

const SimulationBDC2List: React.FC = () => {
  const auth = useAuthUser()
  const authHeader = useAuthHeader()
  const Perss: Utilisateur = JSON.parse(JSON.stringify(auth()))

  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [paramBDC, setParamBDC] = useState<IParametreBDC[]>([]);
  const [data, setData] = useState<ISimulationBDC2[]>([]);
  const [selected, setSelected] = useState<ISimulationBDC2 | null>(null);
  const [sbmit, setSbmit] = useState("");

  // Taux d'intérêt maximum autorisé (du type BDC)
  const [maxTxInt, setMaxTxInt] = useState<number | null>(null);

  // Boutons (même pilotage que /prets/simulation)
  const [ajoutActve, setAjoutActif] = useState(false)
  const [modifActve, setModifActve] = useState(true)
  const [enregActve, setEnregActve] = useState(true)
  const [impActive, setImpActive] = useState(true)
  const [annulActive, setAnnulActive] = useState(true)
  const [consulActive, setConsulActive] = useState(false)

  const [echeancier, setEcheancier] = useState<{
    periode: number;
    dateImput: string;
    interet: number;
    taxe: number;
    netAPayer: number;
  }[]>([]);

  // Picklists
  let searchSimul: ParamsPicklist = { CodeAgence: Perss.codage, Table: "SIMULATIONBDC", Colonne1: "CODSIMUL", Colonne2: "NOMCLI", Colagence: 'CODAGE', Choix: 22 }
  let searchIndice: ParamsPicklist = { CodeAgence: Perss.codage, Table: "CLIENT", Colonne1: "NUMCLI", Colonne2: "NOMCLI", Colagence: 'CODAGE', Choix: 22 }
  let searchTypeBDC: ParamsPicklist = { CodeAgence: Perss.codage, Table: "PARAMBDC", Colonne1: "CODBDC", Colonne2: "LIBELL", Colagence: 'CODAGE', Choix: 22 }

  const columns: ColumnsType<any> = [
    { title: 'Période', dataIndex: 'periode', key: 'periode', width: 80 },
    { title: 'Date imputation', dataIndex: 'dateImput', key: 'dateImput', width: 120 },
    { title: 'Intérêt par période', dataIndex: 'interet', key: 'interet', align: 'right', render: (v: number) => formatNumber(Number(v)) },
    { title: 'Taxe par période', dataIndex: 'taxe', key: 'taxe', align: 'right', render: (v: number) => formatNumber(Number(v)) },
    { title: 'Net par période', dataIndex: 'netAPayer', key: 'netAPayer', align: 'right', render: (v: number) => formatNumber(Number(v)) },
    {
      title: 'Statut', dataIndex: 'statut', key: 'statut', width: 150, align: 'center',
      render: (_: any, r: any) => (
        <Tag color={r.statut === 'Prélevé' ? 'green' : r.statut === 'Annulée' ? 'red' : 'orange'}>
          {r.statut || 'En attente'}{r.statut === 'Prélevé' && r.datPrel ? ` le ${r.datPrel}` : ''}
        </Tag>
      ),
    },
  ];

  // ------------------------------------------------------------------
  // Pilotage des boutons (identique à /prets/simulation)
  // ------------------------------------------------------------------
  function initialiazeButton() {
    setAjoutActif(false)
    setModifActve(true)
    setEnregActve(true)
    setImpActive(true)
    setAnnulActive(true)
    setConsulActive(false)
  }

  const SetStart = () => {
    setSbmit("add")
    setAjoutActif(true)
    setEnregActve(false)
    setAnnulActive(false)
    setConsulActive(true)
  }

  function handleAnulle() {
    setImpActive(true)
    setData([])
    setEcheancier([])
    setSelected(null)
    setMaxTxInt(null)
    setSbmit("")
    form.resetFields()
    form.setFieldsValue({ DATDEBUT: Perss.datjour, DATFIN: Perss.datjour })
    initialiazeButton()
  }

  function cunsultButton() {
    setAnnulActive(false)
    setModifActve(false)
    setEnregActve(true)
    setConsulActive(true)
  }

  function modifButton() {
    setSbmit("update")
    setModifActve(true)
    setAjoutActif(true)
    setEnregActve(false)
    setConsulActive(true)
  }

  // ------------------------------------------------------------------
  // Chargement des listes
  // ------------------------------------------------------------------
  const fetchData = async (): Promise<ISimulationBDC2[]> => {
    try {
      var params = { CodeAgence: Perss.codage, Table: "SIMULATIONBDC", Colonne1: "NUMCLI", Colonne2: "NOMCLI", Colagence: "CODAGE", Init: "", choix: 32, colfiltre: "", valfiltre: "" }
      const res = await fetchDataTable2(Data.ApiUrl.AnySearch, params, authHeader());
      const list: ISimulationBDC2[] = res || []
      setData(list);
      return list
    } catch (err) {
      console.error("Erreur de chargement:", err);
      return []
    }
  };

  const fetchDataTypeBDC = async () => {
    try {
      var params = { CodeAgence: Perss.codage, Table: "PARAMBDC", Colonne1: "CODBDC", Colonne2: "LIBELL", Colagence: "CODAGE", Init: "", choix: 32, colfiltre: "", valfiltre: "" }
      const res = await fetchDataTable2(Data.ApiUrl.AnySearch, params, authHeader());
      if (res.length !== 0) {
        setParamBDC(res);
      } else { console.warn("Aucune donnée reçue !"); }
    } catch (err) {
      console.error("Erreur de chargement:", err);
    }
  };

  // Autofill depuis le type BDC sélectionné
  const onSelectTypeBDC = (codBdc: string) => {
    const typ = paramBDC.find(t => t.CODBDC === codBdc);
    if (typ) {
      setMaxTxInt(Number(typ.TXINT));
      form.setFieldsValue({
        TYPBDC: typ.CODBDC,
        FREQPROV: typ.FREQPROV,
        DUREE: typ.DUREE,
        TYPIMPUTINT: typ.TYPIMPUTINT,
        TXINT: typ.TXINT,
        TXTAXE: typ.TXTAXE,
      });
      NextDate();
    }
  }

  // Plafonne le taux saisi au taux maximum du type
  const plafonnerTaux = (value: any) => {
    const saisi = Number(value);
    if (maxTxInt != null && !isNaN(saisi) && saisi > maxTxInt) {
      SweetAlert.fire({
        icon: "warning",
        title: "Taux maximum dépassé",
        text: `Le taux d'intérêt ne peut pas dépasser ${maxTxInt}% pour ce type de bon.`,
        target: document.body,
      });
      form.setFieldValue("TXINT", maxTxInt);
    }
  }

  // Nombre de jours par fréquence
  const joursFreq = (freq: string) => {
    switch ((freq || "").toUpperCase()) {
      case "A": return 360;
      case "S": return 180;
      case "T": return 90;
      case "M": return 30;
      case "B": return 15;
      case "F": return 60;
      case "H": return 7;
      case "J": return 1;
      default: return 30;
    }
  }

  // Date de fin = date début + fréquence * durée
  function NextDate() {
    const prochaineDate = new Date(form.getFieldValue("DATDEBUT") || Perss.datjour);
    const freq = (form.getFieldValue("FREQPROV") || "").toUpperCase();
    const duree = Number(form.getFieldValue("DUREE")) || 0;
    const jours = joursFreq(freq);
    prochaineDate.setDate(prochaineDate.getDate() + jours * duree);
    form.setFieldValue("DATFIN", prochaineDate.toISOString().split("T")[0]);
  }

  // Calcul de la simulation à partir des valeurs du formulaire ou d'une ligne
  function calculer(source?: any) {
    const get = (f: string) => source ? source[f] : form.getFieldValue(f);

    const freq = (get("FREQPROV") || "").toUpperCase();
    const montant = Number(get("MONTANT")) || 0;
    const tauxInteret = Number(get("TXINT")) || 0;
    const tauxTaxe = Number(get("TXTAXE")) || 0;
    const duree = Number(get("DUREE")) || 0;
    const datDebut = get("DATDEBUT") || Perss.datjour;

    if (!freq || !montant || !tauxInteret || !duree) {
      SweetAlert.fire({
        icon: "warning",
        title: "Champs manquants",
        text: "Veuillez renseigner la fréquence, le montant, le taux et la durée.",
        target: document.body,
        backdrop: true,
      });
      return;
    }

    const prochaineDate = new Date(datDebut);
    const jours = joursFreq(freq);
    prochaineDate.setDate(prochaineDate.getDate() + jours * duree);
    const formattedDate = prochaineDate.toISOString().split("T")[0]

    // 1️⃣ Calcul brut de l'intérêt
    let montIntBrut = (montant * tauxInteret * duree * jours) / 36000;
    // 2️⃣ Calcul provisoire de l'intérêt par période (avant recalage)
    let montProvFreq = Math.round(montIntBrut / duree);
    // 3️⃣ Recalage de l'intérêt total
    let montInt = montProvFreq * duree;
    // 4️⃣ Calcul brut de la taxe (avant arrondis)
    let montTaxeBrut = (montInt * tauxTaxe) / 100;
    // 5️⃣ Calcul provisoire de la taxe périodique
    let montTaxeFreq = Math.round(montTaxeBrut / duree);
    // 6️⃣ Recalage de la taxe totale
    let montTaxe = montTaxeFreq * duree;
    // 7️⃣ Net perçu
    let montNet = montInt - montTaxe;

    // Échéancier
    let dateEcheance = new Date(datDebut);
    const newEcheancier = [];
    for (let i = 1; i <= duree; i++) {
      const prochaine = new Date(dateEcheance);
      prochaine.setDate(prochaine.getDate() + jours);
      newEcheancier.push({
        key: i,
        periode: i,
        dateImput: dateEcheance.toLocaleDateString(),
        interet: montProvFreq,
        taxe: montTaxeFreq,
        netAPayer: montProvFreq - montTaxeFreq,
        statut: 'En attente',   // simulation : aucune dotation réelle
      });
      dateEcheance = prochaine;
    }
    setEcheancier(newEcheancier);

    form.setFieldsValue({
      MTNINT: montInt || 0,
      MTNTAXE: montTaxe || 0,
      MTNTAXEFREQ: montTaxeFreq || 0,
      MTNNETAPAYER: montNet || 0,
      MTNPROVFREQ: montProvFreq || 0,
      DATFIN: formattedDate,
      TotInt: formatNumber(montInt),
      TotTaxe: formatNumber(montTaxe),
      TotNet: formatNumber(montNet),
    });
    setImpActive(false)
  }

  // Exports de l'échéancier
  const handleExport = (format: any) => {
    const title = "Exportation tableau imputaion interets " + (selected?.CODSIMUL || form.getFieldValue("CODSIMUL") || "");
    switch (format) {
      case 'excel':
        GlobalExportToExcel(echeancier, title, "TableauInteretsBDC")
        break;
      case 'pdf':
        GlobalExportToPDF(echeancier, title, "TableauInteretsBDC")
        break;
      case 'word':
        GlobalExportToWord(echeancier, title, "TableauInteretsBDC")
        break;
      case 'csv':
        GlobalExportToCSV(echeancier, title, "TableauInteretsBDC")
        break;
      case 'json':
        GlobalExportToJSON(echeancier, title, "TableauInteretsBDC")
        break;
      default:
        break;
    }
  };

  // Consulter : charge la simulation dont le code est saisi
  const consult = async () => {
    const codsimul = form.getFieldValue("CODSIMUL")
    if (!codsimul) {
      SweetAlert.fire({ title: "Sélectionnez ou saisissez d'abord un code simulation.", icon: "warning" })
      return
    }
    setLoading(true)
    const list = await fetchData()
    setLoading(false)
    const row = list.find(c => c.CODSIMUL === codsimul)
    if (!row) {
      SweetAlert.fire({ title: "Simulation introuvable", text: "Aucune simulation trouvée pour le code : " + codsimul, icon: "warning" })
      return
    }

    const typ = paramBDC.find(t => t.CODBDC === row.TYPBDC);
    if (typ) setMaxTxInt(Number(typ.TXINT));

    const cleanRow: any = {
      ...row,
      DATDEBUT: row.DATDEBUT ? new Date(row.DATDEBUT).toISOString().substring(0, 10) : "",
      DATFIN: row.DATFIN ? new Date(row.DATFIN).toISOString().substring(0, 10) : "",
      lib_NUMCLI: row.NOMCLI,
      lib_TYPBDC: typ?.LIBELL || row.TYPBDC,
      TotInt: formatNumber(Number(row.MTNINT)),
      TotTaxe: formatNumber(Number(row.MTNTAXE)),
      TotNet: formatNumber(Number(row.MTNNETAPAYER)),
    }
    setSelected(row);
    form.setFieldsValue(cleanRow);
    calculer(row)
    cunsultButton()
  }

  useEffect(() => {
    fetchDataTypeBDC()
    form.setFieldsValue({ DATDEBUT: Perss.datjour, DATFIN: Perss.datjour })
  }, []);

  // ------------------------------------------------------------------
  // Soumission
  // ------------------------------------------------------------------
  const onFinish = async () => {
    if (sbmit !== 'add' && sbmit !== 'update') {
      SweetAlert.fire({
        icon: "warning",
        title: "Action requise",
        text: "Cliquez d'abord sur Ajouter (nouvelle simulation) ou Modifier (simulation existante).",
        target: document.body,
      });
      return;
    }

    // Force le calcul avant envoi : garantit MTNINT/MTNTAXE/... et DATFIN cohérents
    calculer();
    // (true) : inclut les valeurs du store sans Form.Item (MTNINT, MTNTAXE, ...)
    const values = form.getFieldsValue(true);
    if (!values.MTNINT || Number(values.MTNINT) === 0) {
      // calculer() a déjà affiché l'avertissement sur les champs manquants
      return;
    }

    const valuesBdc: ISimulationBDC2 = { ...values }
    valuesBdc.NOMCLI = values.lib_NUMCLI || values.NOMCLI
    valuesBdc.CODAGE = Perss.codage
    valuesBdc.UTISAI = Perss.coduti
    valuesBdc.DATCRE = new Date()
    Object.keys(valuesBdc).forEach(k => { if (k.startsWith('lib_') || k.startsWith('Tot')) delete (valuesBdc as any)[k] })

    if (!valuesBdc.NUMCLI || !valuesBdc.NOMCLI) {
      SweetAlert.fire({
        icon: "warning",
        title: "Champs manquants",
        text: "Veuillez renseigner l'indice client, le nom du client.",
        target: document.body,
        backdrop: true,
      });
      return;
    }

    if (Number(valuesBdc.MONTANT) == 0) {
      SweetAlert.fire({
        icon: "warning",
        title: "Champs manquants",
        text: "Veuillez renseigner le montant.",
        target: document.body,
        backdrop: true,
      });
      return;
    }

    if (sbmit == "add") {
      const result = await SweetAlert.fire({
        title: "Création simulation bon de caisse",
        text: `Voulez-vous créer la simulation pour : ${valuesBdc.NOMCLI} ?`,
        showCancelButton: true,
        confirmButtonText: "Oui",
        cancelButtonText: "Non",
        icon: "question",
        confirmButtonColor: "#116599",
        cancelButtonColor: "darkred",
        target: document.body,
      });

      if (result.isConfirmed) {
        setLoading(true);
        let data = Crypter.encrypt(JSON.stringify(valuesBdc));
        let config = {
          method: 'post',
          maxBodyLength: Infinity,
          url: Data.ApiUrl.CreateSimulationBDC,
          headers: {
            'CypherParamBDC': data,
            'Authorization': authHeader(),
            'Content-Type': 'application/json'
          }
        };
        axios.request(config)
          .then((response) => {
            setLoading(false);
            if (response.data) {
              const res = JSON.parse(Crypter.decrypt(response.data));
              let code = res.Result
              if (res.StatutCode == 200) {
                if (code == 'Existe') {
                  SweetAlert.fire({ title: res.Message, text: "Ce code existe déja svp choisissez un autre", icon: "warning" })
                } else {
                  if (code) form.setFieldValue("CODSIMUL", code)
                  fetchData();
                  SweetAlert.fire({ title: res.Message, text: "Code : " + code, icon: "success" })
                  setSbmit("")
                  initialiazeButton()
                  setAjoutActif(false)
                  setModifActve(false)
                  setAnnulActive(false)
                  cunsultButton()
                }
              }
              else {
                SweetAlert.fire({ title: "Warning", text: res.Message, icon: "warning" })
              }
            }
          })
          .catch((error) => {
            setLoading(false);
            console.log(error)
            SweetAlert.fire({ title: "Error Front", text: error, icon: "error" })
          });
      }
    }

    if (sbmit == "update") {
      if (selected && selected.CDETAT != '01') {
        SweetAlert.fire({
          icon: "warning",
          title: "Modification simulation",
          text: "Impossible de mofifier cette simulation: il a déjà fait l'objet d'une validation",
          target: document.body,
          backdrop: true,
        });
        return;
      }

      const result = await SweetAlert.fire({
        title: "Modification simulation bon de caisse",
        text: `Voulez-vous modifier la simulation : ${valuesBdc.CODSIMUL} ?`,
        showCancelButton: true,
        confirmButtonText: "Oui",
        cancelButtonText: "Non",
        icon: "question",
        confirmButtonColor: "#116599",
        cancelButtonColor: "darkred",
        target: document.body,
      });

      if (result.isConfirmed) {
        setLoading(true);
        let data = Crypter.encrypt(JSON.stringify(valuesBdc));
        let config = {
          method: 'put',
          maxBodyLength: Infinity,
          url: Data.ApiUrl.UpdateSimulationBDC,
          headers: {
            'CypherParamBDC': data,
            'Authorization': authHeader(),
            'Content-Type': 'application/json'
          },
          data: data
        };

        axios.request(config)
          .then((response) => {
            setLoading(false);
            if (response.data) {
              const res = JSON.parse(Crypter.decrypt(response.data));
              if (res.StatutCode == 200) {
                let code = res.Result
                if (code == 'INCONNU') {
                  SweetAlert.fire({ title: res.Message, text: "Ce code n'existe pas", icon: "warning" })
                } else {
                  fetchData();
                  SweetAlert.fire({ title: res.Message, text: "Code : " + code, icon: "success" })
                  setSbmit("")
                  initialiazeButton()
                  cunsultButton()
                }
              }
              else {
                SweetAlert.fire({ title: "Warning", text: res.Message, icon: "warning" })
              }
            }
          })
          .catch((error) => {
            setLoading(false);
            console.log(error)
            SweetAlert.fire({ title: "Error Front", text: error, icon: "error" })
          });
      }
    }
  };

  const onFinishFailed = async (errorInfo: any) => {
    var errors: any = [];
    errorInfo.errorFields.map((error: any) => {
      errors.push(error.name + "");
    })
    SweetAlert.fire({ title: "Veuillez remplir tous les champs obligatoires", text: errors.toString().replaceAll(',', " - "), icon: 'warning', confirmButtonText: 'OK' })
  };

  // Réactions de saisie : date de fin + plafond du taux
  const onValuesChange = (changed: any) => {
    if (changed.DATDEBUT !== undefined || changed.DUREE !== undefined || changed.FREQPROV !== undefined) {
      NextDate();
    }
    if (changed.TXINT !== undefined) {
      plafonnerTaux(changed.TXINT);
    }
  };

  // ------------------------------------------------------------------
  // Onglet informations (même layout que /prets/simulation)
  // ------------------------------------------------------------------
  const items: TabsProps['items'] = [
    {
      key: '1',
      label: (
        <span id='Tab'>
          <UserOutlined />
          Information bon de caisse
        </span>
      ),
      children: (
        <div className='tabItem'>
          <div >
            <Box fontStyle="inherit" className='border' width={"85%"} position={"absolute"}>
              <Box display={"flex"} fontStyle="inherit" className='border-left'>

                <Box fontStyle="inherit" className='border-left'>
                  <SearchInput dissabledCode={ajoutActve} notRequired notremove enable width={7} form={form} Titre='Recherche Simulation' label='Code Simul' name='CODSIMUL' Parametre={searchSimul} />
                  <SearchInput width={7} notremove dissabledCode={!ajoutActve} dissabledLib={!ajoutActve} form={form} Titre='Recherche Client' label='Indice client' name='NUMCLI' Parametre={searchIndice} />
                  <InputForm width={11} label='Type imputation' name='TYPIMPUTINT' type='label' notRequired />
                  <InputForm width={11} label='Durée' icon={<SwipeUp color='disabled' fontSize='small' />} name='DUREE' type='number' error='mobile' />
                </Box>

                <Box fontStyle="inherit" className='border-left'>
                  <SearchModal width={7} notremove form={form} Titre='Recherche Type BDC' label='Type BDC' name='TYPBDC' Parametre={searchTypeBDC} handleFunction={onSelectTypeBDC} />
                  <InputForm width={11} label='Fréquence' name='FREQPROV' type='label' notRequired />
                  <InputForm width={11} label={maxTxInt != null ? `Taux intérêt (max ${maxTxInt})` : 'Taux intérêt'} icon={<SwipeUp color='disabled' fontSize='small' />} name='TXINT' type='number' error='mobile' />
                  <InputForm width={11} label='Taux taxe' icon={<SwipeUp color='disabled' fontSize='small' />} name='TXTAXE' type='number' error='mobile' />
                </Box>

                <Box fontStyle="inherit" className='border-left'>
                  <InputForm width={11} label='Montant' icon={<SwipeUp color='disabled' fontSize='small' />} name='MONTANT' type='money' error='mobile' />
                  <InputForm width={11} label='Date début' icon={<SwipeUp color='disabled' fontSize='small' />} name='DATDEBUT' type='date' error='mobile' />
                  <InputForm width={11} label='Date fin' name='DATFIN' type='label' notRequired />
                  <InputForm width={11} label='Intérêt par période (dotation)' name='MTNPROVFREQ' type='label' notRequired />
                  <InputForm width={11} label='Taxe par période' name='MTNTAXEFREQ' type='label' notRequired />
                </Box>

              </Box>
            </Box>
          </div>
        </div>
      ),
    }
  ];

  return (
    <div className='tab'>
      <Typography align='center' className='formTitle'><b>SIMULATION BON DE CAISSE</b></Typography>
      <Spin spinning={loading} size='large' style={{ color: '#116599' }} >
        <Form disabled={!ajoutActve} autoComplete="off" labelAlign='left' form={form} name="dynamic_form_complex" onFinish={onFinish} onFinishFailed={onFinishFailed} onValuesChange={onValuesChange} style={{ minWidth: 'auto', maxWidth: 'auto' }}>

          <div className='bG'>
            <legend><Typography color={"#116599"}>Actions</Typography></legend>
            <Form.Item shouldUpdate className='bouttong'>
              <Space direction="vertical" style={{ width: '100%' }}>
                <Button size='large' className='bt_add' disabled={ajoutActve} onClick={SetStart} type="ghost" htmlType="button" block  >
                  Ajouter
                </Button>
                <Button size='large' className='bt_add' disabled={consulActive} onClick={consult} type="ghost" htmlType="button" block  >
                  Consulter
                </Button>
                <Button size='large' className='bt_add' disabled={enregActve} type="ghost" onClick={() => calculer()} block>
                  Calculer
                </Button>
                <ExportMenu disabled={impActive} onExport={handleExport} />
                <Button size='large' className='bt_add' disabled={modifActve} onClick={modifButton} type="ghost" htmlType="button" block  >
                  Modifier
                </Button>
                <Button size='large' className='bt_add' disabled={enregActve} type="ghost" htmlType="submit" block>
                  Enregistrer
                </Button>
                <Button size='large' disabled={annulActive} onClick={handleAnulle} className='bt_add' type="ghost" htmlType="reset" block>
                  Annuler
                </Button>
              </Space>
            </Form.Item>
          </div>
          <div className='clientP'>
            <div >
              <Tabs size='small' type="card" defaultActiveKey="1" items={items} />
              <Box display={"flex"} fontStyle="inherit" className='border-left tabElement'>
                <Spin spinning={loading}>
                  <Table size='small' style={{ width: 'auto' }} columns={columns} dataSource={echeancier} scroll={{ y: 200, x: 10 }} pagination={false} />
                </Spin>
              </Box>
              <Box display={"flex"} fontStyle="inherit" className='border-left bordure1 tabElement2'>
                <InputForm notRequired disabled width={8} label='Intérêts bruts' name='TotInt' type='text' />
                <InputForm notRequired disabled width={8} label='Taxe retenue' name='TotTaxe' type='text' />
                <InputForm notRequired disabled width={8} label='Net versé au client' name='TotNet' type='text' />
              </Box>
            </div>
            <div>
            </div>
          </div>
        </Form>
      </Spin>

    </div>
  );
}
export default SimulationBDC2List;
